import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faLocationDot, faVideo } from '@fortawesome/free-solid-svg-icons';
import { guestApi } from '../../utils/portalApi';

/**
 * The meetings a guest was invited to (guest role, Phase 1).
 *
 * Joining is the guest meeting page the invitation email already opens
 * (/meet/:token), so the lobby, RSVP and room rules are exactly the ones a
 * guest without an account gets - this list only saves digging out the email.
 */

const when = (meeting) => {
    const start = new Date(meeting.startsAt);
    const day = start.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    if (meeting.allDay) return `${day} · All day`;
    const time = (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `${day} · ${time(meeting.startsAt)}–${time(meeting.endsAt)}`;
};

const GuestMeetings = ({ onAuthError }) => {
    const navigate = useNavigate();
    const [meetings, setMeetings] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        guestApi.get('/meetings').then(({ data }) => setMeetings(data)).catch((err) => {
            onAuthError(err);
            setError(err.response?.data?.message || 'Could not load your meetings.');
            setMeetings([]);
        });
    }, [onAuthError]);

    const now = Date.now();
    const upcoming = (meetings || []).filter((m) => new Date(m.endsAt).getTime() >= now);
    const past = (meetings || []).filter((m) => new Date(m.endsAt).getTime() < now).reverse();

    const card = (m) => (
        <li key={m._id} className="guest-meeting">
            <div className="guest-meeting-body">
                <strong>{m.title}</strong>
                <span>{when(m)}</span>
                {m.organizer && <span>Organised by {m.organizer}</span>}
                {m.location && <span><FontAwesomeIcon icon={faLocationDot} /> {m.location}</span>}
                {m.status === 'live' && <span className="guest-live">Live now</span>}
            </div>
            {m.link && (
                <button type="button" className="portal-primary guest-meeting-open" onClick={() => navigate(m.link)}>
                    {m.hasRoom ? <><FontAwesomeIcon icon={faVideo} /> {m.status === 'live' ? 'Join' : 'Open'}</> : 'Open'}
                </button>
            )}
        </li>
    );

    return (
        <main className="guest-page">
            <h1><FontAwesomeIcon icon={faCalendarDays} /> Meetings</h1>
            {meetings === null && <div className="portal-spinner" />}
            {error && <p className="portal-error">{error}</p>}
            {meetings && !meetings.length && !error && (
                <p className="guest-muted">You have no meetings. Invitations from the team will appear here.</p>
            )}
            {upcoming.length > 0 && (
                <>
                    <h2>Upcoming</h2>
                    <ul className="guest-meetings">{upcoming.map(card)}</ul>
                </>
            )}
            {past.length > 0 && (
                <>
                    <h2>Recent</h2>
                    <ul className="guest-meetings past">{past.map(card)}</ul>
                </>
            )}
        </main>
    );
};

export default GuestMeetings;
